// @ts-nocheck

type DeepPartial<T> = {
	[P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P]
}

const TargetIdSymbol = Symbol('targetId')

let instanceIdNumerator = 1

/**
 *  Creates a deep proxy on top of a target object without keeping a direct reference/pointer to the object.
 *  The underlying target object can be freely replaced without the user of the proxy object need to request
 *  the new instance or worry about references cleanup.
 *
 *  this is achieved by requesting access to the actual target object on invocations and never keeping direct
 *  reference for it internally or exposing it as a return value instead of primitive data types.
 */
export const createReferencelessDeepProxy = <T extends object>(
	targetGetter: () => T,
	targetOverrides: DeepPartial<T> = {}
): T => createReferencelessDeepProxyInternal(targetGetter, targetOverrides)

const createTargetGetterWithInstanceId =
	<T>(_targetGetter: () => T): (() => T) =>
	() => {
		const t = _targetGetter()

		if (!t[TargetIdSymbol]) {
			t[TargetIdSymbol] = instanceIdNumerator
			instanceIdNumerator++
		}
		return t
	}

const createReferencelessDeepProxyInternal = <T extends object>(
	_targetGetter: () => T,
	targetOverrides: DeepPartial<T> = {},
	/** instance id is automatically assigned to the target when it is queried if missing, this has no impact on functionality
	 * but simply help troubleshooting where it is suspected that instance leak might happen, developers can use the instanceIdGetter
	 * in logs to verify what target instance the proxy is accessing */
	_instanceIdGetter?: () => number,
	/** since no reference to the original object can be kept, it can also not be modified directly from the outside.
	 * To support "set" operation and other object mutation functionalities, we keep a separate deeply nested object to store
	 * those external changes and return them to the caller when accessed. this way even if the underlying object is changed,
	 * the external changes are kept from the perspective of the caller.
	 */
	externalChanges? = {}
): T => {
	const isRecursionRoot = !_instanceIdGetter
	const targetGetter = isRecursionRoot ? createTargetGetterWithInstanceId(_targetGetter) : _targetGetter
	const instanceIdGetter = isRecursionRoot ? () => targetGetter()[TargetIdSymbol] : _instanceIdGetter

	return new Proxy({} as T, {
		get(__, propertyKey: string) {
			const target = targetGetter()[propertyKey as keyof T]
			if (typeof target === 'object') {
				externalChanges[propertyKey] ??= {}
				return createReferencelessDeepProxyInternal(
					() => targetGetter()[propertyKey],
					targetOverrides[propertyKey] || {},
					instanceIdGetter,
					externalChanges[propertyKey]
				)
			}

			if (propertyKey in externalChanges) {
				return externalChanges[propertyKey]
			}

			if (propertyKey in targetOverrides) {
				return targetOverrides[propertyKey]
			}

			if (typeof target === 'function') {
				// return a function proxy to prevent caller from getting a reference to the actual underlying function
				return (...args) => targetGetter()[propertyKey](...args)
			}
			// property is not an object nor a function, return the primitive value to the caller
			return targetGetter()[propertyKey]
		},
		// proxy common Object operations to the wrapped target object
		ownKeys: () => Reflect.ownKeys(targetGetter()),
		getOwnPropertyDescriptor: (__, prop) => {
			const target = targetGetter()
			try {
				if (Reflect.has(externalChanges, prop)) {
					return Reflect.getOwnPropertyDescriptor(externalChanges, prop)
				}
				if (Reflect.has(target, prop)) {
					return Reflect.getOwnPropertyDescriptor(target, prop)
				}
			} catch (e) {
				console.error(e)
				return undefined
			}
			return undefined
		},
		has: (_, prop) => prop in targetGetter(),
		getPrototypeOf: () => Object.getPrototypeOf(targetGetter()),
		set: (_, prop, value) => (externalChanges[prop] = value),
		deleteProperty: (_, prop) => delete externalChanges[prop],
		defineProperty: (_, prop, descriptor) => Object.defineProperty(externalChanges, prop, descriptor),
		preventExtensions: () => Object.preventExtensions(targetGetter()),
		isExtensible: () => Object.isExtensible(targetGetter()),
	})
}
